<script lang="ts">
  import { t } from '$core/i18n';
  import type { SessionStore, SessionContext } from '$features/session';
  import {
    listAttemptsBySession,
    listMacroSessionsPage,
    createProblemAttempt,
    updateProblemAttempt,
    getNode
  } from '$db/local/repositories';
  import type { AttemptResult, MacroSession, ProblemAttempt } from '$domain/types';
  import { Icon } from '$ui/primitives/Icon';

  let { store, context }: { store: SessionStore; context?: SessionContext | null } = $props();

  // Si se pasa `context` explícito (Setup), se usa ese.
  // Si no, se usa el contexto de la sesión activa (store.context).
  let effectiveContext = $derived(context !== undefined ? context : store.context);

  interface SessionGroup {
    session: MacroSession;
    attempts: ProblemAttempt[];
  }

  let groups = $state<SessionGroup[]>([]);
  let expandedSessionId = $state<string | null>(null);
  let subtopicName = $state('');
  let loading = $state(true);

  let showModal = $state(false);
  let editingAttempt = $state<ProblemAttempt | null>(null);
  let formNumber = $state('');
  let formDurationSeconds = $state(0);
  let formResult = $state<AttemptResult>('correct');

  function openAdd() {
    editingAttempt = null;
    formNumber = String(store.problemNumber || 1);
    formDurationSeconds = 60;
    formResult = 'correct';
    showModal = true;
  }

  function openEdit(a: ProblemAttempt) {
    editingAttempt = a;
    formNumber = a.problemNumber;
    formDurationSeconds = Math.floor(a.durationMs / 1000);
    formResult = a.result;
    showModal = true;
  }

  async function saveAttempt() {
    const dur = formDurationSeconds * 1000;
    if (editingAttempt) {
      const history = editingAttempt.editHistory || [];
      history.push({
        timestamp: new Date().toISOString(),
        oldDurationMs: editingAttempt.durationMs,
        oldResult: editingAttempt.result
      });
      await updateProblemAttempt(editingAttempt.id, {
        problemNumber: formNumber,
        durationMs: dur,
        result: formResult,
        editHistory: history
      });
    } else {
      const nodeId = effectiveContext?.nodeId;
      if (!store.macroSessionId || !nodeId) return;
      await createProblemAttempt({
        macroSessionId: store.macroSessionId,
        nodeId,
        problemNumber: formNumber,
        durationMs: dur,
        result: formResult
      });
    }
    showModal = false;
    store.dbMutationVersion += 1;
  }

  $effect(() => {
    const ctx = effectiveContext;
    if (!ctx || !ctx.nodeId) {
      subtopicName = '';
      return;
    }
    let cancelled = false;
    void getNode(ctx.nodeId).then((node) => {
      if (cancelled) return;
      subtopicName = node?.title ?? '';
    });
    return () => {
      cancelled = true;
    };
  });

  $effect(() => {
    void store.dbMutationVersion;
    // Deps adicionales: si bumpDbMutation llegara tarde (por race con
    // Dexie), los contadores sincrónicos disparan un segundo tick de
    // re-lectura tras el commit real.
    void store.problemNumber;
    void store.attemptsCorrect;
    void store.attemptsIncorrect;
    void store.attemptsUnresolved;
    const currentId = store.macroSessionId;
    const ctx = effectiveContext;
    let cancelled = false;
    void (async () => {
      const page = await listMacroSessionsPage({ limit: 30 });
      const sessions = page.sessions;
      const loaded: SessionGroup[] = [];
      for (const s of sessions) {
        const rows = await listAttemptsBySession(s.id);
        // Reglas de filtro:
        // 1. SIEMPRE mostramos los attempts de la sesión activa, aunque el
        //    usuario haya cambiado la cadena mid-problem (attemptContext
        //    queda congelado y puede diferir de context).
        // 2. Para sesiones viejas, filtramos por la cadena activa de forma
        //    LENIENTE: solo se exige match en los campos que ctx tenga
        //    seteados. Si ctx tiene subjectId=null, no filtra por subject.
        const filtered = rows.filter((r) => {
          if (currentId && r.macroSessionId === currentId) return true;
          if (!ctx) return true;
          if (ctx.nodeId && r.nodeId !== ctx.nodeId) return false;
          return true;
        });
        if (filtered.length === 0) continue;
        filtered.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
        loaded.push({ session: s, attempts: filtered });
        if (loaded.length >= 10) break;
      }
      if (cancelled) return;
      groups = loaded;
      loading = false;

      const stillExists = expandedSessionId
        ? loaded.some((g) => g.session.id === expandedSessionId)
        : false;
      if (!stillExists) {
        const preferred =
          (currentId && loaded.find((g) => g.session.id === currentId)) ?? loaded[0];
        expandedSessionId = preferred ? preferred.session.id : null;
      }
    })();
    return () => {
      cancelled = true;
    };
  });

  function toggleGroup(id: string) {
    expandedSessionId = expandedSessionId === id ? null : id;
  }

  function badgeClass(result: AttemptResult): string {
    if (result === 'correct') return 'result-c';
    if (result === 'incorrect') return 'result-i';
    return 'result-u';
  }

  function badgeLetter(result: AttemptResult): string {
    if (result === 'correct') return 'C';
    if (result === 'incorrect') return 'I';
    return 'U';
  }

  function resultLabel(result: AttemptResult): string {
    if (result === 'correct') return t('recentProblems.resultCorrect');
    if (result === 'incorrect') return t('recentProblems.resultIncorrect');
    return t('recentProblems.resultUnresolved');
  }

  function formatDuration(ms: number): string {
    const total = Math.floor(ms / 1000);
    const m = Math.floor(total / 60);
    const s = total % 60;
    const d = Math.floor((ms % 1000) / 100);
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}.${d}`;
  }

  function sessionLabel(s: MacroSession): string {
    const modeName =
      s.mode === 'A' ? t('recentProblems.modePractice') : t('recentProblems.modeRecall');
    const d = new Date(s.startedAt);
    const dateStr = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    const timeStr = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    return `${modeName} · ${dateStr} · ${timeStr}`;
  }
</script>

<aside class="recent" aria-label={t('recentProblems.ariaLabel')}>
  <header class="recent-head">
    {#if subtopicName}
      <h3 class="subtopic-name">{subtopicName}</h3>
    {:else}
      <h3 class="subtopic-name subtle">{t('recentProblems.fallbackTitle')}</h3>
    {/if}
    <button class="add-attempt-btn" onclick={openAdd}>
      <Icon name="plus" size={16} /> Add
    </button>
  </header>
  {#if loading}
    <div class="skeleton-list" role="status" aria-label={t('recentProblems.ariaLabel')}>
      {#each Array(3) as _, i (i)}
        <div class="skeleton-row"></div>
      {/each}
    </div>
  {:else if groups.length === 0}
    <p class="empty">{t('recentProblems.empty')}</p>
  {:else}
    <div class="groups">
      {#each groups as g (g.session.id)}
        {@const isOpen = expandedSessionId === g.session.id}
        <div class="group" class:open={isOpen}>
          <button
            type="button"
            class="group-header"
            onclick={() => toggleGroup(g.session.id)}
            aria-expanded={isOpen}
            aria-label={isOpen ? t('recentProblems.groupClose') : t('recentProblems.groupOpen')}
          >
            <span class="group-title">{sessionLabel(g.session)}</span>
            <span class="group-meta">
              <span class="group-count">{g.attempts.length}</span>
              <span class="chev" aria-hidden="true">
                <Icon name="chevron-right" size={12} />
              </span>
            </span>
          </button>
          {#if isOpen}
            <div class="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{t('recentProblems.colNumber')}</th>
                    <th>{t('recentProblems.colTime')}</th>
                    <th>{t('recentProblems.colResult')}</th>
                  </tr>
                </thead>
                <tbody>
                  {#each g.attempts as a (a.id)}
                    <tr class="row" onclick={() => openEdit(a)}>
                      <td class="mono num-cell">{a.problemNumber}</td>
                      <td class="mono">{formatDuration(a.durationMs)}</td>
                      <td>
                        <span
                          class={badgeClass(a.result)}
                          title={resultLabel(a.result)}
                          aria-label={resultLabel(a.result)}
                        >{badgeLetter(a.result)}</span>
                      </td>
                    </tr>
                  {/each}
                </tbody>
              </table>
            </div>
          {/if}
        </div>
      {/each}
    </div>
  {/if}

  {#if showModal}
    <div class="modal-overlay" role="presentation" onclick={() => showModal = false} onkeydown={(e) => e.key === 'Escape' && (showModal = false)}>
      <div class="modal-content" role="dialog" aria-modal="true" tabindex="-1" onclick={(e) => e.stopPropagation()} onkeydown={(e) => e.key === 'Escape' && (showModal = false)}>
        <h3>{editingAttempt ? 'Edit Attempt' : 'Add Attempt'}</h3>
        <div class="form-group">
          <label for="form-number">Problem #</label>
          <input id="form-number" type="text" bind:value={formNumber} />
        </div>
        <div class="form-group">
          <label for="form-duration">Duration (seconds)</label>
          <input id="form-duration" type="number" bind:value={formDurationSeconds} />
        </div>
        <div class="form-group">
          <label for="form-result">Result</label>
          <select id="form-result" bind:value={formResult}>
            <option value="correct">Correct</option>
            <option value="incorrect">Incorrect</option>
            <option value="unresolved">Unresolved</option>
          </select>
        </div>
        <div class="modal-actions">
          <button class="btn-cancel" onclick={() => showModal = false}>Cancel</button>
          <button class="btn-save" onclick={saveAttempt}>Save</button>
        </div>
      </div>
    </div>
  {/if}
</aside>

<style>
  .recent {
    background: var(--color-surface);
    border: var(--border-width) solid var(--color-border);
    border-radius: var(--radius-md);
    padding: var(--space-4);
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    height: 100%;
    min-height: 0;
    transition:
      border-color var(--dur-fast) var(--ease-out),
      transform var(--dur-fast) var(--ease-out),
      box-shadow var(--dur-fast) var(--ease-out);
  }

  .recent:hover {
    border-color: var(--color-border-strong);
    box-shadow: var(--shadow-sm);
  }

  .row {
    cursor: pointer;
  }
  .row:hover {
    background: var(--color-surface-hover);
  }

  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: var(--z-modal);
  }
  .modal-content {
    background: var(--color-surface);
    padding: var(--space-5);
    border-radius: var(--radius-lg);
    width: 90%;
    max-width: 400px;
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    box-shadow: var(--shadow-lg);
  }
  .modal-content h3 {
    margin: 0;
    font-family: var(--font-display);
    font-size: 1.25rem;
    color: var(--color-text-primary);
  }
  .form-group {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }
  .form-group label {
    font-size: var(--text-sm);
    font-weight: 600;
    color: var(--color-text-secondary);
  }
  .form-group input, .form-group select {
    padding: var(--space-2);
    border-radius: var(--radius-sm);
    border: 1px solid var(--color-border);
    background: var(--color-surface-sunken);
    color: var(--color-text-primary);
    font-family: var(--font-body);
    font-size: var(--text-sm);
  }
  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: var(--space-2);
    margin-top: var(--space-2);
  }
  .btn-cancel {
    background: transparent;
    border: none;
    color: var(--color-text-muted);
    cursor: pointer;
    padding: var(--space-2) var(--space-4);
    font-weight: 600;
  }
  .btn-cancel:hover {
    color: var(--color-text-primary);
  }
  .btn-save {
    background: var(--color-brand);
    color: var(--color-brand-fg);
    border: none;
    border-radius: var(--radius-sm);
    padding: var(--space-2) var(--space-4);
    font-weight: 600;
    cursor: pointer;
  }

  .recent-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--space-2);
  }
  .add-attempt-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    background: transparent;
    border: none;
    color: var(--color-brand);
    font-size: var(--text-sm);
    font-weight: 600;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: var(--radius-sm);
  }
  .add-attempt-btn:hover {
    background: var(--color-brand-tint);
  }

  /* Título: Fraunces italic — display editorial (F4a §1).
     El nombre del subtopic es la identidad del panel; sin él, un fallback
     discreto para no duplicar con el heading del shell. */
  .subtopic-name {
    margin: 0;
    font-family: var(--font-display);
    font-size: 1.15rem;
    font-weight: 600;
    letter-spacing: -0.01em;
    line-height: 1.15;
    color: var(--color-text-primary);
    font-style: italic;
  }

  .subtopic-name.subtle {
    font-style: normal;
    font-weight: 500;
    font-size: 1rem;
    color: var(--color-text-muted);
  }

  .empty {
    margin: 0;
    font-size: var(--text-sm);
    color: var(--color-text-muted);
    font-style: italic;
  }

  /* Skeleton de carga (F6 §58, F9 §7.2). 3 filas imitando la altura de
     los grupos. */
  .skeleton-list {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .skeleton-row {
    height: 40px;
    border-radius: var(--radius-sm);
    background: linear-gradient(
      90deg,
      var(--color-surface-sunken) 0%,
      var(--color-surface-hover) 50%,
      var(--color-surface-sunken) 100%
    );
    background-size: 200% 100%;
    animation: skeleton-shimmer 1.4s ease-in-out infinite;
  }

  @keyframes skeleton-shimmer {
    0%   { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }

  @media (prefers-reduced-motion: reduce) {
    .skeleton-row { animation: none; opacity: 0.5; }
  }

  .groups {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .group {
    border: var(--border-width) solid var(--color-border);
    border-radius: var(--radius-sm);
    overflow: hidden;
    background: var(--color-surface);
  }

  .group-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-2);
    width: 100%;
    padding: var(--space-2) var(--space-3);
    background: transparent;
    border: none;
    color: var(--color-text-secondary);
    font-family: var(--font-body, sans-serif);
    font-size: 0.85rem;
    font-weight: 500;
    cursor: pointer;
    text-align: left;
    transition: background var(--dur-fast) var(--ease-out);
  }

  .group-header:hover {
    background: var(--color-bg);
    color: var(--color-text-primary);
  }

  .group-title {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .group-meta {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    flex-shrink: 0;
  }

  .group-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    height: 20px;
    padding: 0 var(--space-1);
    border-radius: var(--radius-full, 9999px);
    background: var(--color-surface);
    color: var(--color-text-muted);
    font-family: var(--font-mono, monospace);
    font-size: 0.75rem;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }

  .chev {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--color-text-muted);
    transition: transform var(--dur-fast) var(--ease-out);
  }

  /* Chevron: cerrado = ▶ (apunta a la derecha, "hay contenido dentro")
     abierto = ▼ (apunta abajo, "está desglosado") */
  .group.open .chev {
    transform: rotate(90deg);
  }

  .table-wrap {
    width: 100%;
    overflow-x: auto;
    border-top: var(--border-width) solid var(--color-border);
  }

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.8rem;
  }

  th {
    text-align: left;
    font-family: var(--font-body, sans-serif);
    font-weight: var(--weight-semibold, 600);
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--color-text-muted);
    padding: var(--space-2);
    border-bottom: var(--border-width) solid var(--color-border);
  }

  td {
    padding: var(--space-2);
    color: var(--color-text-primary);
    border-bottom: var(--border-width) solid var(--color-border);
    vertical-align: middle;
  }

  tr:last-child td {
    border-bottom: none;
  }

  /* Hover sutil en filas para indicar escaneabilidad (F9 §6.5, F10 §6.4). */
  tbody tr.row {
    transition: background var(--dur-fast) var(--ease-out);
  }

  tbody tr.row:hover {
    background: var(--color-surface-hover);
  }

  /* Problema # con tabular-nums y padding mínimo (F9 §4.1 microcopia). */
  .num-cell {
    padding-left: var(--space-3);
    min-width: 48px;
  }

  .mono {
    font-family: var(--font-mono, monospace);
    font-variant-numeric: tabular-nums;
  }

  .result-c,
  .result-i,
  .result-u {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: var(--radius-sm);
    font-family: var(--font-body, sans-serif);
    font-weight: 700;
    font-size: 0.7rem;
    line-height: 1;
    letter-spacing: 0;
    text-transform: uppercase;
  }

  .result-c {
    background: var(--color-success);
    color: var(--color-success-fg);
    border: var(--border-width) solid var(--color-success);
  }

  .result-i {
    background: var(--color-danger);
    color: var(--color-danger-fg);
    border: var(--border-width) solid var(--color-danger);
  }

  .result-u {
    background: var(--color-info);
    color: var(--color-info-fg);
    border: var(--border-width) solid var(--color-info);
  }
</style>